import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef } from 'react';

import { refreshAccessTokenWithExpiredToken } from '@/api/auth';

import { LS_TOKEN_EXPIRES_AT_KEY, LS_TOKEN_KEY } from '@/constants/common';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';

import { signOut } from '@/redux/auth/actions';
import { selectAuthState } from '@/redux/auth/selectors';
import { selectConfigsState } from '@/redux/configs';

import { readLocal } from '@/utils/browser';
import { isCaymanTenant } from '@/utils/tenant';

import {
  getInactiveDeadlineAt,
  isInactiveForSessionTimeout,
  isUserActive,
  startSessionActivityTracking,
  stopSessionActivityTracking,
  subscribeSessionActivity,
} from '@/services/sessionActivity';

const REFRESH_CHECK_INTERVAL_MS = 15 * 1000;

// When the token is about to expire, we refresh it 30 seconds before it expires.
const REFRESH_AHEAD_WINDOW_MS = 30 * 1000;

export function useSessionTimeout() {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAuthState);
  const { tenant } = useAppSelector(selectConfigsState);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | undefined>(
    undefined,
  );
  // Prevent overlapping refresh calls from interval + activity events.
  const isRefreshingRef = useRef(false);

  const cleanUpWhenSignOut = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    isRefreshingRef.current = false;
    stopSessionActivityTracking();
  }, []);

  const refreshTokenIfEligible = useCallback(async () => {
    // Decision 1: keep refresh logic Cayman-only and only for authenticated users.
    if (!user || !isCaymanTenant(tenant) || isRefreshingRef.current) {
      return;
    }

    // Decision 2: refresh is allowed only when user is still "active" by inactivity rules.
    if (!isUserActive()) {
      return;
    }

    const expiresAtFromStorage = readLocal(LS_TOKEN_EXPIRES_AT_KEY);
    const localToken = readLocal(LS_TOKEN_KEY) as string | undefined;
    let expiresAt = expiresAtFromStorage;

    if (typeof expiresAt === 'string') {
      expiresAt = Number(expiresAt);
    }

    if (!localToken || typeof expiresAt !== 'number') {
      return;
    }

    // Refresh only near expiry to avoid unnecessary calls while still preventing 401s.
    const isExpiringSoon = expiresAt - Date.now() <= REFRESH_AHEAD_WINDOW_MS;
    if (!isExpiringSoon) {
      return;
    }

    isRefreshingRef.current = true;
    try {
      await refreshAccessTokenWithExpiredToken(localToken, String(tenant));
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 401) {
        dispatch(signOut());
        cleanUpWhenSignOut();
      }
    } finally {
      isRefreshingRef.current = false;
    }
  }, [dispatch, tenant, user, cleanUpWhenSignOut]);

  useEffect(() => {
    // Current rollout scope: Cayman only.
    if (!isCaymanTenant(tenant)) {
      return;
    }

    if (!user) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      isRefreshingRef.current = false;
      stopSessionActivityTracking();
      return;
    }

    const scheduleSignOut = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const waitTime = Math.max(getInactiveDeadlineAt() - Date.now(), 0);

      timeoutRef.current = setTimeout(() => {
        // Decision 3: sign out exactly at the 10-minute inactivity deadline.
        if (isInactiveForSessionTimeout()) {
          dispatch(signOut());
        } else {
          // If activity resumed right before timeout, recompute the next deadline.
          scheduleSignOut();
        }
      }, waitTime);
    };

    // Process:
    // 1) start activity tracking
    // 2) schedule exact inactivity logout
    // 3) run proactive token refresh checks (interval + activity-triggered)
    startSessionActivityTracking();
    scheduleSignOut();
    void refreshTokenIfEligible();

    refreshIntervalRef.current = setInterval(() => {
      void refreshTokenIfEligible();
    }, REFRESH_CHECK_INTERVAL_MS);

    const unsubscribe = subscribeSessionActivity(() => {
      scheduleSignOut();
      void refreshTokenIfEligible();
    });

    return () => {
      unsubscribe();
      cleanUpWhenSignOut();
    };
  }, [dispatch, refreshTokenIfEligible, tenant, user, cleanUpWhenSignOut]);
}
